// ============================================================================
// SHELL WEB — primitives COMPOSABLES des invariants §24.9sexies-ter, JAMAIS un gabarit rigide
// (décision utilisateur 2026-08-05 : « les mécaniques, pas la mise en page »).
//
// Garanti PAR CONSTRUCTION : rail fixe pleine hauteur, SEUL le contenu défile (c) ; anatomie de la
// marque de rail (a) ; topbar sticky avec bascule thème à droite (b, + variante VITRÉE maquette).
// Reste LIBRE : largeur du rail, contenu du rail et de la topbar (slots), layout des pages, couleurs
// (variables du thème — la personnalité vient de ThemeVars/overrides).
// Une surface qui n'a pas cette anatomie (ex. vitrine Store) n'utilise simplement pas ces primitives.
// ============================================================================
import { createContext, useContext, useEffect, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type Ref } from 'react';
import { RAIL_BRAND, RAIL_BREAKPOINT, RAIL_FOOT, RAIL_PADDING, RAIL_WIDTH, TOPBAR, Z_LAYERS, type IconName } from '@orgazone/ui-tokens';
import { Icon } from './icons';
import { OZ_TOPBAR_ACTION } from './interactive';
import { t } from '@orgazone/i18n';

/**
 * ÉTAT PARTAGÉ RAIL ↔ TOPBAR (passe responsive 2026-08-24). La topbar affiche le BURGER quand le rail
 * est hors écran — par CONTEXTE et non par câblage d'app : une app qui oublierait de poser le bouton
 * n'aurait AUCUN menu sur téléphone, et personne ne le verrait avant un client. Les trois surfaces
 * (BO, Console, EDI) le reçoivent sans une ligne.
 */
const RailContext = createContext<{ mobile: boolean; open: boolean; setOpen: (v: boolean) => void } | null>(null);

/** Suit une media query — SSR/tests sûrs (sans `matchMedia`, répond false et n'écoute rien). */
export function useMediaMax(px: number): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(`(max-width: ${px}px)`).matches);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${px}px)`);
    const h = () => setOn(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [px]);
  return on;
}

export interface AppShellProps {
  /** Contenu du rail (marque, nav, pied) — rendu dans un <aside> fixe qui défile en interne si besoin. */
  rail: ReactNode;
  /**
   * Rembourrage de l'<aside> lui-même. Défaut = `RAIL_PADDING` de la charte (2026-09-08, décision
   * utilisateur : « on aligne toutes les apps sur le back-office »). Il valait `0`, et seul le
   * back-office le posait — les trois autres rails rembourraient leurs propres blocs, chacun à sa
   * valeur, ce qui donnait jusqu'à TROIS colonnes différentes dans un même rail.
   * ⚠️ NE LE SURCHARGER QUE POUR UNE RAISON ÉCRITE : la valeur vient de la maquette, et un rail qui
   * s'en écarte redevient le cas particulier qu'on vient de faire disparaître.
   */
  railPadding?: string;
  /**
   * EN-TÊTE FIXE de la colonne de contenu — bandeau de contexte, topbar. **Il ne défile jamais.**
   *
   * ⚠️ C'EST LUI QUI FAIT QUE SEUL LE BLOC CENTRAL A UN ASCENSEUR (relevé maquette, retour utilisateur
   * 2026-09-04 : « dans la maquette l'intégralité de l'en-tête reste visible ; chez nous le bandeau
   * disparaît en descendant »). Auparavant TOUTE la colonne défilait : la topbar tenait par un
   * `position: sticky`, mais le bandeau de contexte, lui, s'en allait — c'est-à-dire l'élément dont
   * le métier est justement de rappeler EN PERMANENCE qu'on agit ailleurs que chez soi.
   * ⚠️ Un `sticky` ne remplace pas cette structure : il retient un élément, il n'empêche pas la page
   * de défiler sous lui, et l'ascenseur reste celui de la colonne entière — d'où le décalage visible
   * entre notre barre et celle du dessin.
   */
  header?: ReactNode;
  /**
   * CE QUE MONTRE LA PAGE, pour une application dont le rail navigue par des BOUTONS (le store change de vue sans
   * changer d'adresse). Quand il change, le tiroir se referme : la navigation a fini son travail, comme sur un lien.
   * ⚠️ Pas « fermer sur tout bouton » : les groupes dépliants du rail sont des boutons, et le tiroir doit rester
   *    ouvert quand on les déplie.
   */
  navKey?: string;
  /** ⚠️ Défaut = RAIL_WIDTH canonique (240, décision 2026-08-05) — ne surcharger que pour une raison écrite. */
  railWidth?: number;
  /** Colonne de droite (topbar + pages) — c'est ELLE qui défile, jamais la page entière. */
  children: ReactNode;
}

/**
 * Coquille : rail fixe à gauche + colonne défilante à droite (§24.9sexies-ter c, par construction).
 * ⚠️ SOUS `RAIL_BREAKPOINT`, LE RAIL DEVIENT UN TIROIR (2026-08-24) : hors écran, ouvert par le burger
 * de la topbar, refermé par le voile, Échap, ou un clic sur un LIEN de navigation — jamais sur un
 * bouton (les groupes dépliants du rail resteraient inutilisables). Un rail fixe de 240 px mangeait
 * 60 % d'un téléphone et ne laissait rien au contenu.
 */
export function AppShell({ rail, railWidth = RAIL_WIDTH, railPadding = RAIL_PADDING, header, navKey, children }: AppShellProps) {
  const mobile = useMediaMax(RAIL_BREAKPOINT);
  const [open, setOpen] = useState(false);
  // Le tiroir ne survit pas au retour desktop : le rail redevient fixe, l'état s'oublie.
  useEffect(() => { if (!mobile) setOpen(false); }, [mobile]);
  useEffect(() => { setOpen(false); }, [navKey]);
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);
  const calme = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <RailContext.Provider value={{ mobile, open, setOpen }}>
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : `${railWidth}px 1fr`, height: '100vh', overflow: 'hidden' }}>
        {mobile && open && (
          // ⚠️ Plans de l'ÉCHELLE PARTAGÉE, jamais en dur : posé à 80, le tiroir passait SOUS les
          // en-têtes collants de tableau (z 100) — le menu s'ouvrait derrière le journal.
          <div onClick={() => setOpen(false)} aria-hidden style={{ position: 'fixed', inset: 0, zIndex: Z_LAYERS.drawerScrim, background: 'rgba(12,14,16,.45)' }} />
        )}
        <aside
          aria-hidden={mobile && !open ? true : undefined}
          // ⚠️ Fermer sur un LIEN seulement : un `<a href>` navigue (le tiroir a fini son travail) ;
          // un `<button>` du rail est un dépliage de groupe ou une action — le tiroir reste ouvert.
          onClickCapture={mobile ? (e) => { if ((e.target as HTMLElement).closest('a[href]')) setOpen(false); } : undefined}
          style={{
            height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0,
            padding: railPadding,
            background: 'var(--sidebar-bg)', color: 'var(--sidebar-text)', borderRight: '1px solid var(--sidebar-line)',
            ...(mobile ? {
              position: 'fixed', top: 0, bottom: 0, left: 0, width: railWidth, zIndex: Z_LAYERS.drawer,
              transform: open ? 'translateX(0)' : 'translateX(-105%)',
              transition: calme ? undefined : 'transform .22s cubic-bezier(.2,.7,.3,1)',
              boxShadow: open ? '0 0 44px rgba(10,12,14,.45)' : undefined,
            } : null),
          }}
        >
          {rail}
        </aside>
        {/* ⚠️ LA COLONNE NE DÉFILE PAS ; son BLOC CENTRAL défile. `minHeight: 0` est structurel sur le
            bloc : sans lui, un enfant en flex refuse de se comprimer et le conteneur pousse hors de
            l'écran au lieu de faire défiler son contenu. */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: '100vh', overflow: 'hidden' }}>
          {header}
          {/* ⚠️ `scrollbarGutter: stable` : la gouttière est réservée MÊME sur une page courte.
              Sans elle, le contenu s'élargirait de 10 px dès qu'il n'y a rien à faire défiler,
              et le bord droit sauterait d'un écran à l'autre. */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }}>
            {children}
          </div>
        </div>
      </div>
    </RailContext.Provider>
  );
}

export interface RailBrandProps {
  /** Logo de l'app (img/svg, ~30px) — injecté, jamais imposé. */
  logo: ReactNode;
  /** Rôle de la surface, en uppercase (« ÉDITEUR · STAFF », « EXPLOITATION », « BACKOFFICE »…). */
  surtitle: string;
  /** Nom de l'app, en fort — jamais doublé ailleurs dans le rail. */
  name: string;
  /**
   * ORDRE DES DEUX LIGNES — `role` (défaut) met le rôle de la surface AU-DESSUS en teinte de marque
   * (Store, Console, EDI) ; `product` met le PRODUIT au-dessus et sa surface dessous, en mono
   * capitales effacées (maquette back-office).
   * ⚠️ Deux agencements, une seule primitive : la marque de rail est un invariant §24.9sexies-ter a,
   * et une app qui recopierait son propre en-tête de rail sortirait cet invariant de la charte.
   */
  layout?: 'role' | 'product';
}

/** Marque de rail (§24.9sexies-ter a) : logo + deux lignes + ligne séparatrice pleine largeur. */
export function RailBrand({ logo, surtitle, name, layout = 'role' }: RailBrandProps) {
  const t = RAIL_BRAND;
  if (layout === 'product') {
    const p = t.subtitle;
    // ⚠️ AUCUN FILET ICI. Au relevé, le trait appartient au `<nav>` (`border-top`), donc il est EN
    //    RETRAIT du rembourrage de l'aside au lieu de courir d'un bord à l'autre. Posé sur ce bloc,
    //    il en faisait une barre d'en-tête pleine largeur — plus haute à l'œil qu'elle ne l'est.
    return (
      <div style={{ padding: p.padding, display: 'flex', alignItems: 'center', gap: p.gap }}>
        {logo}
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: p.lineHeight, minWidth: 0 }}>
          <span style={{ fontSize: p.nameSize, fontWeight: t.nameWeight, letterSpacing: p.nameSpacing, color: 'var(--sidebar-text)', whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: p.subtitleSize, letterSpacing: p.subtitleSpacing, textTransform: 'uppercase', color: 'var(--sidebar-text-dim)' }}>{surtitle}</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ padding: t.padding, display: 'flex', alignItems: 'center', gap: t.gap, borderBottom: '1px solid var(--sidebar-line)' }}>
      {logo}
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
        <span style={{ fontSize: t.surtitleSize, fontWeight: 700, letterSpacing: t.surtitleSpacing, textTransform: 'uppercase', color: 'var(--teal-500)' }}>{surtitle}</span>
        <span style={{ fontSize: t.nameSize, fontWeight: t.nameWeight, whiteSpace: 'nowrap' }}>{name}</span>
      </div>
    </div>
  );
}

export interface TopbarSearchProps {
  /** Texte saisi. Omis = champ NON contrôlé (l'EDI ne lit la valeur qu'à la validation). */
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  /** Ce que le champ cherche, pour un lecteur d'écran — un `placeholder` disparaît à la saisie. */
  label: string;
  /** Pour piloter le focus (la Console rend la main au champ en fermant son panneau). */
  inputRef?: Ref<HTMLInputElement>;
  /** Pour détecter un clic DEHORS (la Console referme alors son panneau). */
  boxRef?: Ref<HTMLDivElement>;
  /* ⚠️ L'événement de React est importé SOUS ALIAS (`ReactKeyboardEvent`) : sous son nom propre il
     masquerait le `KeyboardEvent` du DOM que la touche Échap du rail écoute vingt lignes plus haut —
     deux types homonymes, dont un seul va à `addEventListener`. */
  /** Panneau de résultats ancré sous la pilule — rendu DANS le conteneur positionné. */
  children?: ReactNode;
}

/**
 * LA RECHERCHE DE LA BARRE DU HAUT — la capsule `.field-search` de la charte, posée une seule fois.
 *
 * ⚠️ 🔴 CE QUI MANQUAIT N'ÉTAIT PAS LA RECETTE, C'ÉTAIT SON EMPLOI. La charte porte `.field-search` —
 * capsule, fond en creux, loupe intégrée — et sept écrans du back-office s'en
 * servent. La Console et l'EDI, eux, dessinaient chacun un RECTANGLE à la main (34 px, `--r-sm`, fond
 * `--canvas`) : trois écritures du même champ, dont deux ignoraient la réponse déjà arbitrée. La maquette
 * EDI demandant la capsule, c'est la charte qui avait raison depuis le début.
 *
 * ⚠️ LA PEAU VIT DANS LA RECETTE, PAS ICI : son état est `:focus-within` et son anneau `:has()`, qu'un
 * style calculé ne sait pas exprimer. Ce composant ne pose que ce qui ne peut pas être une classe —
 * la loupe du REGISTRE partagé (le back-office la redessine à la main sept fois, à reprendre), le champ
 * nu, et le conteneur POSITIONNÉ dont un panneau de résultats a besoin.
 */
export function TopbarSearch({ value, onChange, onFocus, onKeyDown, placeholder, label, inputRef, boxRef, children }: TopbarSearchProps) {
  return (
    <div ref={boxRef} className="field-search topbar" style={{ position: 'relative' }}>
      <Icon name="search" size={TOPBAR.searchIcon} />
      <input
        ref={inputRef}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        aria-label={label}
      />
      {children}
    </div>
  );
}

export interface TopbarProps {
  /** Fil de page et tout contenu gauche/central (slots libres : recherche EDI, santé, etc.). */
  children?: ReactNode;
  /** Actions de DROITE (cloche « À traiter », etc.) — rendues juste avant la bascule thème. */
  actions?: ReactNode;
  /** VITRÉE (maquette Console) : translucide + blur, le contenu défile dessous. Sinon opaque (EDI). */
  glass?: boolean;
  /**
   * Thème courant + bascule (useWebTheme) — le bouton vit ICI, nulle part ailleurs (b).
   * ⚠️ Une application SANS thème sombre (le store) ne passe ni l'un ni l'autre : la bascule n'est pas dessinée, un
   *    bouton qui ne bascule rien ferait semblant (§23.0).
   */
  mode?: 'light' | 'dark';
  onToggleTheme?: () => void;
  style?: CSSProperties;
}

/** Topbar sticky (§24.9sexies-ter b) : slots libres à gauche, bascule thème 30×30 TOUT à droite.
 *  ⚠️ Sur mobile, elle OUVRE LE TIROIR : le burger vient du contexte d'AppShell, automatiquement —
 *  aucune app n'a à le poser, donc aucune ne peut l'oublier. */
export function Topbar({ children, actions, glass, mode, onToggleTheme, style }: TopbarProps) {
  const rail = useContext(RailContext);
  return (
    /* ⚠️ PLUS DE `position: sticky` (2026-09-04) : la barre vit dans l'en-tête FIXE de la coquille,
       qui ne défile pas. Un `sticky` y serait un mécanisme sans effet — et un mécanisme sans effet
       finit par être lu comme la raison pour laquelle la barre tient, ce qui est faux. */
    <header style={{
      minHeight: TOPBAR.minHeight, padding: TOPBAR.padding,
      // ⚠️ `minHeight` et non `height` PRÉCISÉMENT parce que la barre se replie : une hauteur figée
      //    découperait la seconde ligne au lieu de la laisser grandir.
      display: 'flex', alignItems: 'center', flexWrap: TOPBAR.wrap, gap: `${TOPBAR.rowGap}px ${TOPBAR.gap}px`, flexShrink: 0,
      // ⚠️ VITRÉE = le voile `--head` de l'app quand elle en définit un (maquette Console : un blanc à
      // 5 % dégradé vers 1,2 %, qui laisse VOIR le fond flouté). Le `color-mix` sur `--paper` n'est
      // qu'un repli : à 82 % il rend le papier presque opaque, donc une barre d'une AUTRE couleur que
      // le reste de la page — le défaut signalé le 2026-08-11.
      background: glass ? `var(--head, color-mix(in srgb, var(--paper) ${TOPBAR.glassPaperPct}%, transparent))` : 'var(--paper)',
      backdropFilter: glass ? TOPBAR.glassBlur : undefined,
      borderBottom: '1px solid var(--ink-200)',
      // ⚠️ LA BARRE PREND SA PLACE DANS LA PILE, au cran des en-têtes : le flou d'une barre VITRÉE en fait un contexte
      //    d'empilement, qui enfermait les déroulés de la barre (cloche « À traiter », résultats de recherche) ; les
      //    blocs de la page, peints après elle, passaient dessus et les coupaient. Au-dessus de la page, sous les
      //    tiroirs et les modales.
      position: 'relative', zIndex: Z_LAYERS.stickyHeader,
      ...style,
    }}>
      {rail?.mobile && (
        <button
          type="button"
          onClick={() => rail.setOpen(true)}
          aria-label={t('Ouvrir le menu')}
          aria-expanded={rail.open}
          // ⚠️ DEUX PIXELS DE PLUS que les autres contrôles (retour utilisateur 2026-08-24) : c'est LE
          // bouton du pouce sur téléphone, il mérite la cible la plus large de la barre.
          style={{
            width: TOPBAR.control + 2, height: TOPBAR.control + 2, borderRadius: 'var(--r-ctrl)',
            border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-600)',
            display: 'grid', placeItems: 'center', cursor: 'pointer', flexShrink: 0,
          }}
        >
          <Icon name="menu" size={TOPBAR.controlIcon} />
        </button>
      )}
      {children}
      <div style={{ flex: 1 }} />
      {actions}
      {mode && onToggleTheme && <ThemeToggle mode={mode} onToggle={onToggleTheme} />}
    </header>
  );
}

export interface RailFootProps {
  /** Nom affiché (les initiales de l'avatar en dérivent). */
  name: string;
  /** Rôle/portée sous le nom (« Éditeur », « Support · lecture seule »…). */
  role: string;
  /** Détail au survol du nom (ex. email) / du rôle (ex. permissions techniques). */
  nameTitle?: string;
  roleTitle?: string;
  onLogout: () => void;
  /**
   * LE NOM OUVRE LE COMPTE — fourni par une app qui a un écran « Mon compte » (le back-office). Absent, le pied reste
   * une identité inerte, comme dans les autres applications.
   * ⚠️ C'EST LE NOM, PAS UNE ENTRÉE DE MENU : ce qu'on règle là ne vaut que pour soi (langue, mot de passe, sessions),
   *    quand le menu « Réglages » règle le MAGASIN. La maquette du back-office met donc la porte sur la personne.
   */
  account?: RailFootAccount;
}

/** Ce que le pied doit savoir pour ouvrir l'écran du compte. */
export interface RailFootAccount {
  onOpen: () => void;
  /** L'écran du compte est celui qu'on regarde : le nom prend alors la sélection du rail. */
  active: boolean;
  /** Ce que le bouton ouvre, en clair — infobulle et nom accessible. */
  title: string;
}

/**
 * Les initiales d'un nom pour l'avatar du pied de rail : la première lettre des deux premiers mots (« M. Bloch » → MB,
 * « Jean Dupont » → JD) ; un nom d'un seul mot garde ses deux premières lettres (« Support » → SU).
 * ⚠️ LES LETTRES, PAS LES CARACTÈRES : prendre les deux premiers caractères donnait « M. » pour « M. Bloch ».
 */
export function initialsOf(name: string): string {
  const words = (name || '').trim().split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  if (words.length === 0) return '—';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Pied de rail (§24.9sexies-ter k — modèle Store privé) : avatar d'initiales marque-700 + nom +
 *  rôle + bouton icône Déconnexion. Encres du rail (suivent son identité claire/sombre). */
export function RailFoot({ name, role, nameTitle, roleTitle, onLogout, account }: RailFootProps) {
  // ⚠️ LE SURVOL EST UN ÉTAT, PAS UNE DÉCORATION PERMANENTE. La maquette pose ces boutons
  //    `background:transparent` avec un `style-hover` — le rendu affichait le fond de survol EN
  //    PERMANENCE, donc un bouton qui paraissait enfoncé en permanence (relevé 2026-09-05).
  const [survol, setSurvol] = useState(false);
  const [survolCompte, setSurvolCompte] = useState(false);
  const a = RAIL_FOOT.account;

  const avatar = (
    <span style={{
      width: RAIL_FOOT.avatar, height: RAIL_FOOT.avatar, borderRadius: '50%', background: 'var(--teal-700)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: RAIL_FOOT.avatarFontSize, fontWeight: 700, flexShrink: 0,
    }}>
      {initialsOf(name)}
    </span>
  );
  // ⚠️ SUR LE NOM QUI OUVRE LE COMPTE, LES ENCRES SONT CELLES DU RAIL : sélection teal-700 et texte blanc quand
  //    l'écran est ouvert, texte atténué au repos — comme les autres destinations du rail (maquette, `railFg`).
  const nameColor = account ? (account.active ? '#fff' : 'var(--sidebar-text-dim)') : 'var(--sidebar-text)';
  const texts = (
    // ⚠️ HAUTEUR DE LIGNE EXPLICITE : sans elle, ces deux lignes héritaient de celle du CORPS de
    //    l'app, et le pied changeait de taille selon la surface (Console 60,3 px contre 59 ailleurs,
    //    mesuré 2026-09-08). Une primitive partagée ne se laisse pas dimensionner par la feuille qui
    //    l'entoure. Voir `RAIL_FOOT.lineHeight`.
    <span style={{ display: 'block', flex: 1, minWidth: 0, lineHeight: RAIL_FOOT.lineHeight }}>
      <span title={nameTitle} style={{ display: 'block', fontSize: RAIL_FOOT.nameSize, fontWeight: RAIL_FOOT.nameWeight, color: nameColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      {/* ⚠️ LE RÔLE SE BORNE À UNE LIGNE, comme le nom au-dessus. Le rail rembourré a rétréci cette
          colonne de 24 px, et « Support · lecture seule » (Console) s'y repliait sur deux lignes :
          le pied passait alors à 66,4 px contre 59 ailleurs, c'est-à-dire une hauteur qui dépend du
          LIBELLÉ de l'utilisateur connecté. ⚠️ Rien n'est perdu pour autant : le texte complet est
          toujours à l'infobulle, `roleTitle` quand une app a mieux à dire, le rôle lui-même sinon —
          une troncature sans recours serait un écran qui ment (§23.0). Le curseur « aide », lui,
          reste réservé au cas où il y a VRAIMENT une explication en plus. */}
      <span title={roleTitle ?? role} style={{ display: 'block', fontSize: RAIL_FOOT.roleSize, color: 'var(--sidebar-text-dim)', cursor: roleTitle ? 'help' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role}</span>
    </span>
  );
  const logout = (
    <button
      type="button"
      onClick={onLogout}
      title={t('Déconnexion')}
      aria-label={t('Déconnexion')}
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      onFocus={() => setSurvol(true)}
      onBlur={() => setSurvol(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: RAIL_FOOT.buttonSize, height: RAIL_FOOT.buttonSize,
        borderRadius: RAIL_FOOT.buttonRadius, background: survol ? 'var(--sidebar-hover)' : 'transparent', border: 'none', color: 'var(--sidebar-text-dim)', cursor: 'pointer', flexShrink: 0,
      }}
    >
      <Icon name="logout" size={RAIL_FOOT.iconSize} stroke={RAIL_FOOT.iconStroke} />
    </button>
  );

  if (!account) {
    return (
      <div style={{ marginTop: RAIL_FOOT.marginTop, padding: RAIL_FOOT.padding, borderTop: '1px solid var(--sidebar-line)', display: 'flex', alignItems: 'center', gap: RAIL_FOOT.gap }}>
        {avatar}
        {texts}
        {logout}
      </div>
    );
  }
  return (
    <div style={{
      marginTop: RAIL_FOOT.marginTop, padding: `${a.padTop}px ${a.padRight}px ${a.padBottom}px ${a.padLeft}px`,
      borderTop: '1px solid var(--sidebar-line)', display: 'flex', alignItems: 'center', gap: a.gap,
    }}>
      <button
        type="button"
        onClick={account.onOpen}
        title={account.title}
        aria-label={account.title}
        aria-current={account.active ? 'page' : undefined}
        onMouseEnter={() => setSurvolCompte(true)}
        onMouseLeave={() => setSurvolCompte(false)}
        onFocus={() => setSurvolCompte(true)}
        onBlur={() => setSurvolCompte(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: RAIL_FOOT.gap, flex: 1, minWidth: 0,
          padding: a.buttonPad, marginLeft: -a.buttonPad, border: 'none', borderRadius: RAIL_FOOT.buttonRadius,
          background: account.active ? 'var(--teal-700)' : survolCompte ? 'var(--sidebar-hover)' : 'transparent',
          textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
        }}
      >
        {avatar}
        {texts}
      </button>
      {logout}
    </div>
  );
}

/** Bascule thème 30×30 (soleil/lune) — utilisée par Topbar ; exportée pour les surfaces sans topbar. */
export interface TopbarActionProps {
  icon: IconName;
  /** Ce que fait le bouton, en clair — c'est l'`aria-label` ET l'infobulle. */
  label: string;
  onClick?: () => void;
  /**
   * PAS ENCORE CÂBLÉ : le bouton reste DESSINÉ et atteignable au clavier, mais inerte, et il porte
   * « à venir » dans son infobulle.
   * ⚠️ C'est la règle du 2026-09-02 (« où est le bouton ? ») : une action qu'on retire de la barre
   * laisse croire qu'elle n'existera jamais, ou qu'on l'a oubliée. Elle se DIT inactive.
   */
  soon?: boolean;
  /** Pourquoi c'est inactif — remplace l'infobulle par défaut quand on a mieux à dire. */
  reason?: string;
  /** Contenu superposé (pastille de compte). ⚠️ JAMAIS un nombre inventé — voir §23.0. */
  badge?: ReactNode;
  /**
   * LES ATTRIBUTS D'UN DÉCLENCHEUR DE PANNEAU (`Popover`), quand ce bouton en ouvre un.
   *
   * ⚠️ 🔴 POURQUOI ILS PASSENT PAR ICI plutôt que par un bouton écrit à la main à côté : la boîte de ce
   * contrôle (taille, bordure, teinte, position de la pastille) est CELLE de la barre. Un second bouton
   * écrit pour l'occasion aurait divergé au premier ajustement — c'est exactement ce que cette primitive
   * a été extraite pour empêcher, la bascule de thème ayant déjà posé la même boîte de son côté.
   * ⚠️ On ne prend pas l'objet du `Popover` tel quel : ce paquet ne dépend pas de son module, et il n'a
   * besoin que de la `ref` et des trois attributs ARIA.
   */
  trigger?: {
    ref: Ref<HTMLButtonElement>;
    'aria-expanded': boolean;
    'aria-haspopup': 'dialog' | 'listbox' | 'menu';
    'aria-controls': string;
  };
}

/**
 * UN CONTRÔLE DE LA BARRE — cloche, bascule de thème, et tout bouton-icône qui viendra s'y poser.
 *
 * ⚠️ POURQUOI UNE PRIMITIVE POUR UN BOUTON. La bascule de thème posait déjà sa boîte à la main, et la
 * cloche allait poser la même une deuxième fois. Deux boîtes identiques écrites séparément finissent
 * par différer d'un pixel de rayon ou d'une nuance de bordure, et personne ne sait plus laquelle est
 * la bonne. Ici la géométrie vient de `TOPBAR.control` — la même que la capsule magasin et la
 * recherche, ce qui est tout l'intérêt.
 *
 * ⚠️ UN BOUTON DÉSACTIVÉ N'ÉMET AUCUN ÉVÉNEMENT DE SOURIS, donc son `title` ne s'affiche jamais.
 * L'infobulle est portée par l'enveloppe, exactement comme `Button` le fait pour son `reason`.
 */
export function TopbarAction({ icon, label, onClick, soon, reason, badge, trigger }: TopbarActionProps) {
  const infobulle = reason ?? (soon ? t('{label} — à venir, pas encore construit.', { label }) : label);
  return (
    <span title={infobulle} style={{ display: 'inline-flex', flexShrink: 0 }}>
      <button
        type="button"
        {...(trigger ?? {})}
        onClick={soon ? undefined : onClick}
        aria-label={infobulle}
        aria-disabled={soon || undefined}
        // ⚠️ Bordure, fond et encre viennent de la classe (survol, panneau ouvert, inactif) : en ligne, ils l'emporteraient.
        className={OZ_TOPBAR_ACTION}
        style={{
          position: 'relative',
          width: TOPBAR.control, height: TOPBAR.control, borderRadius: 'var(--r-ctrl)',
          display: 'grid', placeItems: 'center', cursor: soon ? 'not-allowed' : 'pointer', flexShrink: 0,
        }}
      >
        <Icon name={icon} size={TOPBAR.controlIcon} />
        {badge}
      </button>
    </span>
  );
}

export function ThemeToggle({ mode, onToggle }: { mode: 'light' | 'dark'; onToggle: () => void }) {
  return (
    <TopbarAction
      icon={mode === 'dark' ? 'sun' : 'moon'}
      label={mode === 'dark' ? t('Passer en clair') : t('Passer en sombre')}
      onClick={onToggle}
    />
  );
}
